import React, { useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { naira, formatApiError } from "../../lib/format";
import api from "../../lib/api";
import MoneyField from "./MoneyField";

/**
 * Inline editor for a draft lease's financial terms. Mirrors the backend
 * formula: commission auto-recalculates when rent/caution/rate change unless
 * the user explicitly sets a custom commission_amount.
 */
export default function LeaseFinancialsForm({ lease, onCancel, onSaved }) {
  const [draft, setDraft] = useState({
    rent_amount: lease.rent_amount ?? 0,
    caution_fee: lease.caution_fee ?? 0,
    agreement_fee: lease.agreement_fee ?? 0,
    service_charge: lease.service_charge ?? 0,
    commission_rate: lease.commission_rate ?? 7,
    commission_amount: lease.commission_amount ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => {
    setDraft((prev) => {
      const next = { ...prev, [k]: v };
      if (["rent_amount", "caution_fee", "commission_rate"].includes(k)) {
        const rent = Number(next.rent_amount) || 0;
        const caution = Number(next.caution_fee) || 0;
        const rate = Number(next.commission_rate) || 0;
        next.commission_amount = Math.round((rent + caution) * (rate / 100));
      }
      return next;
    });
  };

  const total =
    (Number(draft.rent_amount) || 0) +
    (Number(draft.caution_fee) || 0) +
    (Number(draft.agreement_fee) || 0) +
    (Number(draft.service_charge) || 0) +
    (Number(draft.commission_amount) || 0);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/leases/${lease.id}/financials`, draft);
      toast.success("Financial terms updated");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="financials-edit-form">
      <div className="bg-[#FDF6E3]/40 border border-[#EBDDA7] rounded-lg p-3 text-xs text-[#A0801C]">
        Editing financial terms for this draft lease. Commission auto-recalculates when you change Rent, Caution Fee, or Rate. You can also manually set a custom Commission. Lease must still be in <b>draft</b> status — once sent or signed, terms are locked.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <MoneyField label="Rent (annual)" value={draft.rent_amount} onChange={(v) => update("rent_amount", v)} testid="fin-rent"/>
        <MoneyField label="Caution Fee" value={draft.caution_fee} onChange={(v) => update("caution_fee", v)} testid="fin-caution"/>
        <MoneyField label="Agreement Fee" value={draft.agreement_fee} onChange={(v) => update("agreement_fee", v)} testid="fin-agreement"/>
        <MoneyField label="Service Charge" value={draft.service_charge} onChange={(v) => update("service_charge", v)} testid="fin-service"/>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Commission Rate (%)</span>
          <input
            type="number" step="0.1" min={0} max={100}
            value={draft.commission_rate}
            onChange={(e) => update("commission_rate", e.target.value)}
            className="input-modav mt-1"
            data-testid="fin-rate"
          />
        </label>
        <MoneyField
          label="Commission Amount"
          value={draft.commission_amount}
          onChange={(v) => update("commission_amount", v)}
          testid="fin-commission"
          hint={`Auto: ${naira(Math.round(((Number(draft.rent_amount) || 0) + (Number(draft.caution_fee) || 0)) * ((Number(draft.commission_rate) || 0) / 100)))}`}
        />
      </div>
      <div className="bg-[#0E5C3A]/5 border border-[#0E5C3A]/20 rounded-lg p-3 flex items-center justify-between" data-testid="fin-preview-total">
        <span className="text-sm text-[#5E7166] font-semibold">New TOTAL PAYABLE</span>
        <span className="naira-total text-2xl">{naira(total)}</span>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="btn-ghost" data-testid="fin-cancel-btn">Cancel</button>
        <button onClick={save} disabled={saving} className="btn-gold" data-testid="fin-save-btn">
          <Save size={14}/> {saving ? "Saving…" : "Save & Recalculate"}
        </button>
      </div>
    </div>
  );
}
